import { useNavigate, useParams } from 'react-router-dom';
import { CalendarRange, Download } from 'lucide-react';
import { formatTimeDisplay } from '@peles/shared';
import { EventTypeSelect } from '../components/common';
import { exportTimeline } from '../features/timelinePortable';
import { ImportTimelineButton } from '../components/event-ops/ImportTimelineButton';
import { useTimeline, useUpdateTimeline } from '../hooks/useEventOps';
import { TimelineEditor } from '../components/timeline/TimelineEditor';
import { TimelineEditorSkeleton } from '../components/timeline/TimelineEditorSkeleton';
import { timeToInputValue } from '../components/timeline/entryForm';

// Re-exported for existing consumers/tests; the implementations live in shared helpers.
export { formatTimeDisplay, timeToInputValue };

/** A timeline template (`/templates/timeline/:templateId`): the editor, plus its event type and export/import. */
export function TimelineTemplateEditPage() {
  const { templateId } = useParams<{ templateId: string }>();
  const navigate = useNavigate();
  const { data: timeline, isLoading } = useTimeline(templateId!);
  const updateTimeline = useUpdateTimeline();

  if (isLoading) return <TimelineEditorSkeleton />;
  if (!timeline) {
    return (
      <div className="p-6 text-red-500" data-testid="not-found">
        Template not found.
      </div>
    );
  }

  return (
    <TimelineEditor
      timeline={timeline}
      backPath="/templates"
      badge="Timeline Template"
      actions={
        <>
          <button
            type="button"
            onClick={() => exportTimeline(timeline)}
            className="flex items-center gap-1.5 min-h-11 sm:min-h-0 px-3 py-1.5 text-sm text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50"
            title="Export timeline as JSON"
            data-testid="export-timeline-button"
          >
            <Download size={14} aria-hidden />
            Export
          </button>
          <ImportTimelineButton isTemplate={true} onSuccess={() => navigate('/templates')} />
        </>
      }
      details={
        <div
          className="bg-white rounded-xl border border-gray-200 shadow-sm p-4"
          data-testid="template-event-type"
        >
          <div className="flex items-center gap-1.5 text-sm font-medium text-gray-700 mb-2">
            <CalendarRange size={14} aria-hidden />
            Event type
          </div>
          <EventTypeSelect
            value={timeline.eventTypeId}
            onChange={(eventTypeId) =>
              updateTimeline.mutate({ id: timeline.id, updates: { eventTypeId } })
            }
          />
          <p className="mt-1 text-xs text-gray-500">
            Suggested for new events of this type; questionnaire templates of the same type can link
            to it.
          </p>
        </div>
      }
    />
  );
}
